import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Ability, CommonEquipmentService, CommonGlobalService, dayjs, Equipment, Option, RootState, Skill } from '@umanforge/common';
import { TaskEquipmentService } from 'services/task_equipment.service';
import { EquipmentAbility } from '@umanforge/common/dist/interfaces/ability.interfaces';
import { EquipmentSkill } from '@umanforge/common/dist/interfaces/skill.interfaces';

export function useEquipment( sectorId: string, taskId?: string ) {
  const token = useSelector( ( state: RootState ) => {return state.token.value;} );
  const dispatch = useDispatch();

  // Modal
  const [ isFirstRender, setIsFirstRender ] = useState( true );

  const [ selectedEquipmentsSaved, setSelectedEquipmentsSaved ] = useState<Option<Equipment>[]>( [] );
  const [ equipmentsOptions, setEquipmentsOptions ] = useState<Option<Equipment>[]>( [] );
  const [ selectedEquipments, setSelectedEquipments ] = useState<Option<Equipment>[]>( [] );

  const [ equipmentAbilities, setEquipmentAbilities ] = useState<Option<Ability>[]>( [] );
  const [ equipmentSkills, setEquipmentSkills ] = useState<Option<Skill>[]>( [] );

  const [ equipmentAbilitiesSave, setEquipmentAbilitiesSave ] = useState<EquipmentAbility[]>( [] );
  const [ equipmentSkillsSave, setEquipmentSkillsSave ] = useState<EquipmentSkill[]>( [] );

  const [ equipmentsChanged, setEquipmentsChanged ] = useState<boolean>( false );

  // Use Effects
  useEffect( () => {
    init();
  }, [ ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      const _refresh = async () => {
        await refreshEquipments( selectedEquipments );
        setEquipmentsChanged( checkChanged() );
      };
      _refresh();
    }
  }, [ selectedEquipments ] );

  const init = async () => {
    const response: Equipment[] = ( await CommonEquipmentService.getAllowedEquipmentsBySectorsId( [ sectorId ], token, dispatch ) );
    setEquipmentsOptions( CommonGlobalService.toOptions( response ) );

    if ( taskId ) {
      const equipments: Equipment[] = await TaskEquipmentService.getEquipmentsByTaskId( taskId, token, dispatch );
      const options = CommonGlobalService.toOptions( equipments );
      setSelectedEquipments( options );
      setSelectedEquipmentsSaved( options );

      if ( equipments.length > 0 ) {
        refreshEquipments( options );
      }
    }

    setIsFirstRender( false );
  };

  const getEquipmentErrors = ( equipment: Option<Equipment>,  requiredEquiAbilities: string[], requiredEquiSkills: { id: string; iluo: number | undefined }[] ) : { isExpired: boolean, skillRequired: boolean, abilityRequired: boolean } => {
    const isExpired = dayjs( equipment.object.expirationDate ).isBefore( dayjs().endOf( 'day' ) );
    const abilityRequired = equipmentAbilitiesSave.filter( ea => { return ea.equipmentId == equipment.object.id; } ).some( ea => {
      return requiredEquiAbilities.includes( ea.abilityId );
    } );

    const skillRequired = equipmentSkillsSave
      .filter( es => { return es.equipmentId == equipment.object.id; } )
      .some( es => {
        return requiredEquiSkills
          .filter( res => { return res.id == es.skillId; } )
          .some( res => { return ( res.iluo ?? 1 ) <= es.iluo; } );
      } );

    return {
      isExpired: isExpired,
      skillRequired,
      abilityRequired
    };
  };


  // API request

  const refreshEquipments = async ( equipments: Option<Equipment>[] ) => {
    if ( equipments.length > 0 ) {
      const [
        _abilities,
        _skills,
        _equipmentAbilities,
        _equipmentSkills,
      ] = await Promise.all( [
        CommonEquipmentService.getAbilitiesByEquipmentsId( equipments.map( equipment => { return equipment.object.id; } ), token, dispatch ),
        CommonEquipmentService.getSkillsByEquipmentsId( equipments.map( equipment => { return equipment.object.id; } ), token, dispatch ),
        CommonEquipmentService.getEquipmentAbilitiesByEquipmentsId( equipments.map( equipment => { return equipment.object.id; } ), token, dispatch ),
        CommonEquipmentService.getEquipmentSkillsByEquipmentsId( equipments.map( equipment => { return equipment.object.id; } ), token, dispatch )
      ] );

      setEquipmentAbilities( CommonGlobalService.toOptions( _abilities ) );
      setEquipmentSkills( CommonGlobalService.toOptions( _skills ) );
      setEquipmentAbilitiesSave( _equipmentAbilities );
      setEquipmentSkillsSave( _equipmentSkills );
    }
    else {
      setEquipmentAbilities( [] );
      setEquipmentSkills( [] );
    }
  };

  const checkChanged = (): boolean => {
    return (
      selectedEquipments.map( equipment => { return equipment.object.id; } ).sort().join()
      !=
      selectedEquipmentsSaved.map( equipment => { return equipment.object.id; } ).sort().join()
    );
  };

  return {
    selectedEquipments, setSelectedEquipments,
    selectedEquipmentsSaved,
    equipmentAbilities,
    equipmentSkills,
    equipmentsOptions,
    equipmentsChanged,

    getEquipmentErrors
  };
};


